import type { FC } from "react";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { AlertTriangle, CalendarClock, Check, History } from "lucide-react";
import {
  DOCUMENT_VERSION_STATUS_LABELS,
  type DocumentVersionStatus,
} from "@/models/document/DocumentVersion";

/**
 * A version that has not taken effect yet is as easy to act on by mistake as
 * an outdated one, so only `current` is styled calmly.
 */
const STATUS_STYLE: Record<DocumentVersionStatus, string> = {
  current: "border-success/30 bg-success/10 text-success",
  older: "border-warning/30 bg-warning/10 text-warning",
  future: "border-primary/30 bg-primary/10 text-primary",
  // Neutral on purpose: green reads as "gældende" everywhere else in the app
  // (cf. StatusBadge), which is the opposite of what this state means.
  latest: "border-border bg-muted text-muted-foreground",
};

const STATUS_ICON: Record<DocumentVersionStatus, FC<{ className?: string }>> = {
  current: Check,
  older: AlertTriangle,
  future: CalendarClock,
  latest: History,
};

type DocumentVersionStatusBadgeProps = {
  status: DocumentVersionStatus;
  /** Overrides the status wording, e.g. "Version 2" for a workspace revision. */
  label?: string;
  /** Overrides the hover wording when `label` says something broader than the status. */
  title?: string;
  className?: string;
};

/**
 * States the version status in words rather than leaving it to be inferred
 * from a date or a revision number (LAR-423).
 */
const DocumentVersionStatusBadge: FC<DocumentVersionStatusBadgeProps> = ({
  status,
  label,
  title,
  className,
}) => {
  const Icon = STATUS_ICON[status];
  const statusText = DOCUMENT_VERSION_STATUS_LABELS[status].long;

  return (
    <Badge
      variant="outline"
      // The visible text is the accessible name; `title` is a hover hint that
      // spells out the status when `label` replaces it with something broader.
      title={title ?? statusText}
      className={cn(STATUS_STYLE[status], className)}
    >
      <Icon className="shrink-0" />
      {label ?? statusText}
    </Badge>
  );
};

export default DocumentVersionStatusBadge;
